import {
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/pro-regular-svg-icons";
import axios from "axios";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import slugify from "slugify";

import { useAllAWSAccounts } from "src/adminPanel/queries";
import { WorkspaceWithSettings } from "src/api/types";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { WorkspaceSettings } from "src/clients/flow-api";
import { AwsRegionType, AWS_REGIONS } from "src/constants/AwsRegions";
import { DOCS_RETENTION_POLICIES } from "src/constants/ExternalLinks";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { DataRetentionUnitDropdown } from "src/layout/WorkspaceFormModal/DataRetentionUnitDropdown";
import { RegionComboBox } from "src/layout/WorkspaceFormModal/RegionComboBox";
import { FORM_DEFAULT_VALUES } from "src/layout/WorkspaceFormModal/constants";
import {
  getNameErrorMessage,
  nameValidations,
} from "src/layout/WorkspaceFormModal/utils";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { formatDate } from "src/utils/datetime";
import {
  getStringValidations,
  getWorkspaceSlugErrorMessage,
  WORKSPACE_SLUG_CONSTRAINTS,
} from "src/utils/validations";

type WorkspaceSettingsModalProps = {
  variant: "Create" | "Edit";
  isOpen: boolean;
  workspace?: WorkspaceWithSettings;
  wsId?: string;
  onClose: () => void;
  onSubmit: (data: CreateWorkspaceForm) => void;
};

export type CreateWorkspaceForm = {
  name: string;
  slug: string;
  region: AwsRegionType;
  aws_account_id?: string;
  flow_services_version?: string;
  settings?: WorkspaceSettings;
};

export const WorkspaceSettingsModal: React.FC<WorkspaceSettingsModalProps> = ({
  variant,
  isOpen,
  workspace: currentWorkspace,
  onClose,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    reset,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<CreateWorkspaceForm>({
    defaultValues: FORM_DEFAULT_VALUES,
    shouldFocusError: true,
    mode: "onChange",
  });

  const { data: awsAccounts } = useAllAWSAccounts();

  useEffect(() => {
    const currentWorkspaceRegion = AWS_REGIONS.find(
      (region) => region.identifier === currentWorkspace?.aws_region,
    );
    const currentWorkspaceName = currentWorkspace?.name;

    if (currentWorkspaceRegion && currentWorkspaceName) {
      reset({
        name: variant === "Edit" ? currentWorkspaceName : "",
        slug: "",
        region: currentWorkspaceRegion,
        aws_account_id: currentWorkspace?.aws_account_id,
        settings: {
          data_retention: currentWorkspace?.settings.data_retention,
          data_wiping_enabled_until:
            currentWorkspace?.settings.data_wiping_enabled_until,
        },
        ...(isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && {
          flow_services_version: currentWorkspace?.flow_services_version,
        }),
      });
    }
  }, [currentWorkspace, reset, variant]);

  const onFormConfirm = handleSubmit(async (data: CreateWorkspaceForm) => {
    try {
      await onSubmit(data);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 422) {
        setError("slug", { type: "notAvailable" }, { shouldFocus: true });
      } else {
        toastActions.failure({
          title: "Error submitting workspace configuration",
          description: "Please try again later.",
        });
      }
    }
  });

  return (
    <Modal afterLeave={() => reset()} open={isOpen} onClose={onClose}>
      <Modal.Header>{variant} workspace</Modal.Header>
      <form onSubmit={onFormConfirm}>
        <Modal.Content>
          <div className="flex flex-col gap-6">
            <div>
              <Label required>Workspace name</Label>
              <Input
                fullWidth
                {...register("name", {
                  required: true,
                  validate: nameValidations,
                  onChange: (e) => {
                    if (variant === "Create") {
                      setValue(
                        "slug",
                        slugify(e.target.value, { strict: true, lower: true }),
                        {
                          shouldValidate: true,
                        },
                      );
                    }
                  },
                })}
              />
              {errors.name && (
                <ErrorHint height="h-2.5">
                  {getNameErrorMessage(errors.name.type)}
                </ErrorHint>
              )}
            </div>
            {variant === "Create" && (
              <div>
                <Label required>Slug</Label>
                <Input
                  fullWidth
                  {...register("slug", {
                    required: true,
                    validate: getStringValidations(WORKSPACE_SLUG_CONSTRAINTS),
                  })}
                />
                {errors.slug && (
                  <ErrorHint height="h-2.5">
                    {getWorkspaceSlugErrorMessage(errors.slug.type)}
                  </ErrorHint>
                )}
              </div>
            )}
            <div>
              <Label required>Region</Label>
              <Controller
                control={control}
                name="region"
                render={({ field: { onChange, value } }) => (
                  <RegionComboBox
                    disabled={variant === "Edit"}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <div>
              <Label mb="mb-1" required>
                Retention policy
              </Label>
              <Tooltip
                body="Setting a data retention period will automatically delete Decision Flow history after the specified time period. This might impact analytics, backtesting, and other features."
                footerAction={{
                  text: "Read more",
                  onClick: () => window.open(DOCS_RETENTION_POLICIES, "_blank"),
                }}
                placement="top"
                title="Retention policy"
              >
                <Icon color="text-gray-500" icon={faInfoCircle} size="2xs" />
              </Tooltip>
              <div className="mb-2 text-gray-500 font-inter-normal-12px">
                Automatically delete decision history older than
              </div>

              <div>
                <div className="flex items-center gap-x-2">
                  <Input
                    type="number"
                    fullWidth
                    {...register("settings.data_retention.value", {
                      required: "This field cannot be empty",
                      min: {
                        value: 0,
                        message: "The value must be a positive integer",
                      },
                      valueAsNumber: true,
                    })}
                    errored={!!errors.settings?.data_retention?.value}
                  />
                  <div className="w-32 shrink-0">
                    <Controller
                      control={control}
                      name="settings.data_retention.unit"
                      render={({ field: { value, onChange } }) => (
                        <DataRetentionUnitDropdown
                          value={value}
                          onChange={onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {errors.settings?.data_retention?.value && (
                  <ErrorHint>
                    {errors.settings?.data_retention?.value?.message}
                  </ErrorHint>
                )}
                {variant === "Edit" && (
                  <div className="mt-4">
                    <Label mb="mb-1">Data wiping</Label>
                    <Controller
                      control={control}
                      name="settings.data_wiping_enabled_until"
                      render={({ field: { value, onChange } }) => (
                        <>
                          {!value || new Date(value) < new Date() ? (
                            <div className="flex items-center gap-x-2">
                              <div className="text-gray-500 font-inter-normal-12px">
                                Data wiping is disabled
                              </div>
                              <Button
                                iconLeft={faExclamationTriangle}
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  onChange(
                                    new Date(
                                      Date.now() + 4 * 60 * 60 * 1000,
                                    ).toISOString(),
                                  );
                                }}
                              >
                                Enable for 4 hours
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-x-2">
                              <div className="text-gray-500 font-inter-normal-12px">
                                Data wiping enabled until {formatDate(value)}
                              </div>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  onChange(undefined);
                                }}
                              >
                                Disable
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
            {isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && (
              <div className="mt-8 text-gray-500">
                <div className="mb-1 text-gray-800 font-inter-semibold-13px">
                  [⚡ POWERTOOL] Workspace services version
                </div>
                <Input
                  fullWidth
                  {...register("flow_services_version", {
                    required: false,
                    validate: (value) =>
                      value?.length === 0 || value?.includes("/"),
                  })}
                />
              </div>
            )}
            {isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && (
              <div>
                <Label mb="mb-1" required>
                  AWS Account
                </Label>
                <Controller
                  control={control}
                  name="aws_account_id"
                  render={({ field: { onChange, value } }) => (
                    <div>
                      <Select
                        disabled={variant === "Edit"}
                        options={
                          awsAccounts?.map((account) => ({
                            key: account.id,
                            value: (
                              <span>
                                {account.name}
                                <span className="ml-1.5 text-gray-400">
                                  ({account.account_id})
                                </span>
                              </span>
                            ),
                          })) ?? []
                        }
                        value={value}
                        onChange={onChange}
                      />
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              disabled={!isValid || isSubmitting}
              htmlType="submit"
              loading={isSubmitting}
              variant="primary"
            >
              Save
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
